import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import {
  activeList,
  clientList,
  currencyselect,
  newselectOption,
} from "../../../core/common/selectoption/selectoption";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { setActivityTogglePopup } from "../../../core/data/redux/commonSlice";
import { all_routes } from "../../router/all_routes";
import { highlightMatch } from "../../../core/common/highlightmatch";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import {
  fetchAllInvoices,
  fetchInvoicesByLeadOwner,
  Invoice,
} from "../../../api/invoiceproposal";
import { RootState } from "../../../core/data/redux/store";

const InvoiceGrid = () => {
  const route = all_routes;
  const dispatch = useDispatch();
  const [selectedDate1, setSelectedDate1] = useState<Date | null>(new Date());
  const handleDateChange1 = (date: Date | null) => {
    setSelectedDate1(date);
  };
  const toggleEditInvoice = () => {
    setEditInvoice(!editInvoice);
  };
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };
  const [editInvoice, setEditInvoice] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const [error, setError] = useState<string | null>(null);
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(today.getDate() - 30);
  oneMonthAgo.setHours(0, 0, 0, 0);

  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: oneMonthAgo,
    end: today,
  });

  const activityToggle = useSelector(
    (state: any) => state?.activityTogglePopup
  );

  // Filter and Sort Function
  const useFilteredAndSortedInvoices = (
    invoices: Invoice[],
    searchText: string,
    sortOption: string,
    dateRange: { start: Date | null; end: Date | null },
    statusFilter: string
  ) => {
    return useMemo(() => {
      let filtered = invoices.filter((inv) => {
        const fields = [
          inv.invoice_proposal_id,
          inv.status,
          inv.deal?.product?.product_name,
          inv.deal?.contact?.company_name,
          inv.deal?.contact?.contact_person_name,
          inv.deal?.contact?.company_mail,
        ];

        const matchesSearch = fields.some((field) =>
          field?.toString().toLowerCase().includes(searchText.toLowerCase())
        );

        const invoiceDate = inv.invoice_date
          ? new Date(inv.invoice_date)
          : null;
        const matchesDate =
          !dateRange.start ||
          !dateRange.end ||
          (invoiceDate &&
            invoiceDate >= new Date(dateRange.start) &&
            invoiceDate <= new Date(dateRange.end));

        const matchesStatus =
          statusFilter === "all" ||
          inv.status?.toLowerCase() === statusFilter.toLowerCase();

        return matchesSearch && matchesDate && matchesStatus;
      });

      // Sorting logic
      if (sortOption === "asc") {
        filtered.sort((a, b) =>
          (a.invoice_proposal_id || "").localeCompare(
            b.invoice_proposal_id || ""
          )
        );
      } else if (sortOption === "desc") {
        filtered.sort((a, b) =>
          (b.invoice_proposal_id || "").localeCompare(
            a.invoice_proposal_id || ""
          )
        );
      } else if (sortOption === "recentlyViewed") {
        // 👇 Sort by created_at
        filtered.sort(
          (a, b) =>
            new Date(b.created_at || "").getTime() -
            new Date(a.created_at || "").getTime()
        );
      } else if (sortOption === "recentlyAdded") {
        // 👇 Sort by invoice_date
        filtered.sort(
          (a, b) =>
            new Date(b.invoice_date || "").getTime() -
            new Date(a.invoice_date || "").getTime()
        );
      }

      return filtered;
    }, [invoices, searchText, sortOption, dateRange, statusFilter]);
  };

  const filteredAndSortedInvoices = useFilteredAndSortedInvoices(
    invoices,
    searchText,
    sortOption,
    dateRange,
    statusFilter
  );

  const handleDateApply = (event: any, picker: any) => {
    setDateRange({
      start: picker.startDate.toDate(),
      end: picker.endDate.toDate(),
    });
  };

  const currencyCodeMap: { [key: string]: string } = {
    INR: "INR",
    Dollar: "USD",
    Euro: "EUR",
    Pound: "GBP",
  };
  const userId = useSelector((state: RootState) => state.auth.user_id);

  // Update loadInvoices function to use fetchInvoicesByLeadOwner
  const loadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      if (userId) {
        const data = await fetchInvoicesByLeadOwner(userId);
        setInvoices(data);
      }
    } catch (err) {
      setError("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <>
        <PageLoadProgressBar />
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                {/* Page Header */}
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title">
                        <i className="ti ti-file-invoice text-primary me-2" />
                        Invoices
                        <span className="count-title">{invoices.length}</span>
                      </h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <Link to={route.InvoiceList}>
                          <i className="ti ti-arrow-narrow-left" />
                          Invoices
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* /Page Header */}
                {/* Search */}
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <div className="icon-form mb-3 mb-sm-0">
                      <span className="form-icon">
                        <i className="ti ti-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search Invoices"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end"></div>
                  </div>
                </div>
                {/* /Search */}
                {/* Filter */}
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 border-top mt-4 pt-4 mb-4">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="dropdown-toggle bg-transparent"
                        data-bs-toggle="dropdown"
                      >
                        {statusFilter === "all"
                          ? "All Invoices"
                          : statusFilter.charAt(0).toUpperCase() +
                            statusFilter.slice(1)}
                      </Link>
                      <div className="dropdown-menu dropdown-menu-start">
                        <ul className="list-unstyled mb-0">
                          {[
                            { label: "All Invoices", value: "all" },
                            { label: "Paid", value: "paid" },
                            { label: "Notpaid", value: "notpaid" },
                          ].map(({ label, value }) => {
                            const isActive = statusFilter === value;
                            return (
                              <li key={value}>
                                <Link
                                  to="#"
                                  onClick={() => setStatusFilter(value)}
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${
                                    isActive ? "fw-bold text-primary" : ""
                                  }`}
                                >
                                  <span>
                                    <i className="ti ti-dots-vertical me-2" />
                                    {label}
                                  </span>
                                  {isActive && <i className="ti ti-check" />}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="dropdown-toggle bg-white"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-sort-ascending-2 me-2" /> Sort
                      </Link>
                      <div className="dropdown-menu dropdown-menu-start">
                        <ul className="list-unstyled mb-0">
                          {[
                            { label: "Ascending", value: "asc" },
                            { label: "Descending", value: "desc" },
                            {
                              label: "Recently Viewed",
                              value: "recentlyViewed",
                            },
                            { label: "Recently Added", value: "recentlyAdded" },
                          ].map(({ label, value }) => {
                            const isActive = sortOption === value;
                            return (
                              <li key={value}>
                                <Link
                                  to="#"
                                  onClick={() => setSortOption(value)}
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${
                                    isActive ? "fw-bold text-primary" : ""
                                  }`}
                                >
                                  <span>
                                    <i className="ti ti-circle-chevron-right me-1" />
                                    {label}
                                  </span>
                                  {isActive && <i className="ti ti-check" />}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    <div className="icon-form">
                      <span className="form-icon">
                        <i className="ti ti-calendar" />
                      </span>
                      <DateRangePicker
                        initialSettings={{
                          ...initialSettings,
                          startDate: dateRange.start,
                          endDate: dateRange.end,
                          ranges: {
                            Today: [new Date(), new Date()],
                            Yesterday: [
                              new Date(
                                new Date().setDate(new Date().getDate() - 1)
                              ),
                              new Date(
                                new Date().setDate(new Date().getDate() - 1)
                              ),
                            ],
                            "Last 7 Days": [
                              new Date(
                                new Date().setDate(new Date().getDate() - 7)
                              ),
                              new Date(),
                            ],
                            "Last 30 Days": [
                              new Date(
                                new Date().setDate(new Date().getDate() - 30)
                              ),
                              new Date(),
                            ],
                            "This Month": [
                              new Date(
                                new Date().getFullYear(),
                                new Date().getMonth(),
                                1
                              ),
                              new Date(),
                            ],
                            "Last Month": [
                              new Date(
                                new Date().getFullYear(),
                                new Date().getMonth() - 1,
                                1
                              ),
                              new Date(
                                new Date().getFullYear(),
                                new Date().getMonth(),
                                0
                              ),
                            ],
                          },
                        }}
                        onApply={handleDateApply}
                      >
                        <input
                          className="form-control bookingrange"
                          type="text"
                        />
                      </DateRangePicker>
                    </div>
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="view-icons">
                      <Link to={route.InvoiceList}>
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link to={route.InvoiceGrid} className="active">
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>
                {/* /Filter */}
                {/* Dynamic Invoice Grid */}
                <div className="row row-cols-1 row-cols-md-3 g-3">
                  {filteredAndSortedInvoices.map((inv, index) => (
                    <div className="col" key={inv.invoice_proposal_id || index}>
                      <div className="card border h-100 invoice-card">
                        <div className="card-body p-3">
                          {/* Header */}
                          <div className="d-flex align-items-center justify-content-between mb-2 border-bottom pb-2">
                            <div className="users-profile">
                              <span
                                className={`badge ${
                                  inv.status === "paid"
                                    ? "bg-success"
                                    : inv.status === "partiallypaid"
                                    ? "bg-warning"
                                    : "bg-danger"
                                }`}
                              >
                                {inv.status === "paid"
                                  ? "Paid"
                                  : inv.status === "partiallypaid"
                                  ? "Partially Paid"
                                  : "notpaid"}
                              </span>
                            </div>
                          </div>

                          {/* Deal Info */}
                          <div className="d-block">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <div className="d-flex align-items-center">
                                <Link
                                  to="#"
                                  className="avatar avatar-sm border flex-shrink-0 me-2"
                                >
                                  <ImageWithBasePath
                                    src="assets/img/priority/truellysel.svg"
                                    className="w-auto h-auto"
                                    alt="Deal"
                                  />
                                </Link>
                                <div>
                                  <h6 className="mb-0">
                                    <Link to="#">
                                      {highlightMatch(
                                        inv.deal?.product?.product_name ||
                                          "N/A",
                                        searchText
                                      )}
                                    </Link>
                                  </h6>
                                </div>
                              </div>
                              <div>
                                <span className="badge badge-purple-light">
                                  #
                                  {highlightMatch(
                                    inv.invoice_proposal_id ?? "N/A",
                                    searchText
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Financial Info */}
                            <div className="mb-2">
                              <p className="mb-1">
                                <strong>Contact:</strong>{" "}
                                {highlightMatch(
                                  inv.deal?.contact?.contact_person_name ||
                                    "N/A",
                                  searchText
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>Email:</strong>{" "}
                                {highlightMatch(
                                  inv.deal?.contact?.company_mail || "N/A",
                                  searchText
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>Invoice Date:</strong>{" "}
                                {highlightMatch(
                                  inv.invoice_date
                                    ? new Date(
                                        inv.invoice_date
                                      ).toLocaleDateString()
                                    : "N/A",
                                  searchText
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>Due Date:</strong>{" "}
                                {highlightMatch(
                                  inv.validation_date
                                    ? new Date(
                                        inv.validation_date
                                      ).toLocaleDateString()
                                    : "N/A",
                                  searchText
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>Currency:</strong>{" "}
                                {highlightMatch(
                                  inv.deal?.currency || "N/A",
                                  searchText
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>Subtotal:</strong>{" "}
                                {(inv.subtotalamount ?? 0).toLocaleString(
                                  "en-IN",
                                  {
                                    style: "currency",
                                    currency:
                                      currencyCodeMap[
                                        inv.deal?.currency || "INR"
                                      ] || "USD",
                                  }
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>cgst:</strong>{" "}
                                {(inv.cgst ?? 0).toLocaleString("en-IN", {
                                  style: "currency",
                                  currency:
                                    currencyCodeMap[
                                      inv.deal?.currency || "INR"
                                    ] || "USD",
                                })}
                              </p>
                              <p className="mb-1">
                                <strong>sgst:</strong>{" "}
                                {(inv.sgst ?? 0).toLocaleString("en-IN", {
                                  style: "currency",
                                  currency:
                                    currencyCodeMap[
                                      inv.deal?.currency || "INR"
                                    ] || "USD",
                                })}
                              </p>
                              <p className="mb-1">
                                <strong>igst:</strong>{" "}
                                {(inv.igst ?? 0).toLocaleString("en-IN", {
                                  style: "currency",
                                  currency:
                                    currencyCodeMap[
                                      inv.deal?.currency || "INR"
                                    ] || "USD",
                                })}
                              </p>
                              <p className="mb-1">
                                <strong>Discount:</strong>{" "}
                                {(inv.discount ?? 0).toLocaleString("en-IN", {
                                  style: "currency",
                                  currency:
                                    currencyCodeMap[
                                      inv.deal?.currency || "INR"
                                    ] || "USD",
                                })}
                              </p>
                              <p className="mb-0">
                                <strong>Total:</strong>{" "}
                                {(inv.invoice_total_amount ?? 0).toLocaleString(
                                  "en-IN",
                                  {
                                    style: "currency",
                                    currency:
                                      currencyCodeMap[
                                        inv.deal?.currency || "INR"
                                      ] || "USD",
                                  }
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Client Info */}
                          <div className="bg-light-400 rounded p-2">
                            <div className="d-flex align-items-center">
                              <Link
                                to="#"
                                className="avatar avatar-sm bg-white border me-2"
                              >
                                <ImageWithBasePath
                                  src="assets/img/icons/company-icon-01.svg"
                                  className="w-auto h-auto"
                                  alt="img"
                                />
                              </Link>
                              <div className="d-flex flex-column">
                                <span className="d-block">Client</span>
                                <Link to="#" className="text-default">
                                  {highlightMatch(
                                    inv.deal?.contact?.company_name || "N/A",
                                    searchText
                                  )}
                                  <br />
                                  {highlightMatch(
                                    inv.deal?.contact?.contact_person_name ||
                                      "N/A",
                                    searchText
                                  )}
                                  <br />
                                  {highlightMatch(
                                    inv.deal?.contact?.company_mail || "N/A",
                                    searchText
                                  )}
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="load-btn text-center pb-4">
                  <Link to="#" className="btn btn-primary">
                    Load More Invoices
                    <i className="ti ti-loader" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
      <> </>
    </div>
  );
};
export default InvoiceGrid;
